// Hostnames whose subdomains map 1:1 to an agency's `public_handle`.
// e.g. `acme.focusagency.site` → tenant handle "acme".
//
// Add additional tenant-root domains here as you wire them up.
// `focuscasting.com` is also a tenant root: bare host + www stay platform (see
// PLATFORM_HOSTNAMES below, which is checked first), but any other subdomain
// like `agency.focuscasting.com` resolves to an agency by handle.
// Note: `focusagency.site` is intentionally NOT a tenant root - the entire
// domain (root + www) belongs to Focus Agency and is resolved via the
// `agency_websites.custom_domain` lookup (custom-domain mode).
export const TENANT_ROOT_DOMAINS = ["focuscasting.com"] as const;

// Subdomains on a tenant root that should NOT be treated as a tenant handle
// (reserved for marketing / system use).
const RESERVED_SUBDOMAINS = new Set<string>(["www", "app", "api", "admin", "mail", "status"]);

export type TenantMode = "platform" | "subdomain" | "custom-domain" | "tenant-root" | "unknown";

export interface TenantHostInfo {
  hostname: string;
  mode: TenantMode;
  /** Present when mode === "subdomain". */
  handle?: string;
  /** Present when mode === "custom-domain". */
  customDomain?: string;
}

const PLATFORM_HOSTNAMES = new Set<string>([
  "focuscasting.com",
  "www.focuscasting.com",
  "focuscasting.lovable.app",
  "localhost",
  "127.0.0.1",
]);

function isLovableInternal(hostname: string): boolean {
  return (
    hostname.endsWith(".lovable.app") ||
    hostname.endsWith(".lovableproject.com") ||
    hostname.endsWith(".lovable.dev")
  );
}

export function getCurrentHostname(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname.toLowerCase();
}

export function resolveTenantHost(hostname: string = getCurrentHostname()): TenantHostInfo {
  const host = hostname.toLowerCase();

  if (!host) return { hostname: host, mode: "unknown" };

  if (PLATFORM_HOSTNAMES.has(host) || isLovableInternal(host)) {
    return { hostname: host, mode: "platform" };
  }

  for (const root of TENANT_ROOT_DOMAINS) {
    if (host === root || host === `www.${root}`) {
      return { hostname: host, mode: "tenant-root" };
    }
    if (host.endsWith(`.${root}`)) {
      const sub = host.slice(0, -1 * (root.length + 1));
      // Multi-level subdomain (e.g. "a.b.focusagency.site") → unsupported.
      if (sub.includes(".")) return { hostname: host, mode: "unknown" };
      if (RESERVED_SUBDOMAINS.has(sub)) return { hostname: host, mode: "tenant-root" };
      return { hostname: host, mode: "subdomain", handle: sub };
    }
  }

  // Anything else is treated as a CNAME'd custom domain.
  return { hostname: host, mode: "custom-domain", customDomain: host };
}
